import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Form, Input, Select, Button, Divider, message, Tag, Checkbox, Tooltip, Icon } from 'antd';

class GroupCreationPanel extends Component {
  constructor(props) {
    super(props);
    this.state = {
      groupName: '',
      groupCode: '',
      customCode: false,
      codeValidationError: null,
      selectedTypes: [],
      isOptional: false,
      isSaving: false,
    };
  }

  handleNameChange = (e) => {
    const newName = e.target.value;
    this.setState({ groupName: newName });

    // Auto-generate code from name if not using custom code
    if (!this.state.customCode && newName) {
      const autoCode = this.generateCodeFromName(newName);
      this.setState({ groupCode: autoCode });
      this.validateCode(autoCode);
    }
  }

  handleCodeChange = (e) => {
    const newCode = e.target.value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .substring(0, 10);

    this.setState({
      groupCode: newCode,
      customCode: true,
    });
    this.validateCode(newCode);
  }

  generateCodeFromName = (name) => {
    // Generate base code from name, limited to 10 characters (URL-safe)
    return name
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .substring(0, 10);
  }

  validateCode = (code) => {
    const { existingGroups, selectedGroupCodes } = this.props;

    // Check if code already exists in the current package
    const isInCurrentPackage = selectedGroupCodes && selectedGroupCodes.includes(code);
    if (isInCurrentPackage) {
      this.setState({
        codeValidationError: 'This group is already added to the current package',
      });
      return;
    }

    // Check if code already exists in the organization
    const isDuplicate = existingGroups.some(g => g.code === code);
    this.setState({
      codeValidationError: isDuplicate ? 'Code already exists in this organization' : null,
    });
  }

  handleTypesChange = (selectedTypes) => {
    this.setState({ selectedTypes });
  }

  handleOptionalChange = (e) => {
    this.setState({ isOptional: e.target.checked });
  }

  handleSave = () => {
    const { groupName, groupCode, selectedTypes, isOptional, codeValidationError } = this.state;
    const { onSave } = this.props;

    // Validation
    if (!groupName || !groupName.trim()) {
      message.error('Group name is required');
      return;
    }

    if (!groupCode || !groupCode.trim()) {
      message.error('Group code is required');
      return;
    }

    if (codeValidationError) {
      message.error(codeValidationError);
      return;
    }

    if (selectedTypes.length === 0) {
      message.error('At least one accreditation type is required');
      return;
    }

    this.setState({ isSaving: true });

    onSave({
      name: groupName.trim(),
      code: groupCode.trim(),
      is_optional: isOptional,
      group_members: selectedTypes.map(type => ({
        type: type,
        guiding_text: null,
      })),
    })
      .then(() => {
        this.setState({ isSaving: false });
      })
      .catch(() => {
        this.setState({ isSaving: false });
      });
  }

  handleCancel = () => {
    this.props.onCancel();
  }

  render() {
    const { accreditationTypes } = this.props;
    const { groupName, groupCode, codeValidationError, selectedTypes, isSaving } = this.state;

    // Group accreditation types by displayTab
    const groupedTypes = accreditationTypes.reduce((acc, type) => {
      const tab = type.displayTab || 'Other';
      if (!acc[tab]) {
        acc[tab] = [];
      }
      acc[tab].push(type);
      return acc;
    }, {});

    // Get sorted tab names
    const sortedTabs = Object.keys(groupedTypes).sort().reverse();

    return (
      <div className="group-creation-panel">
        <div className="creation-form">
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
              <label style={{ marginRight: '8px', fontWeight: 'bold', minWidth: '100px' }}>
                Group Name:
              </label>
              <Input
                placeholder="Working With Children Check"
                value={groupName}
                onChange={this.handleNameChange}
                style={{ flex: 1 }}
              />
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
              <label style={{ marginRight: '8px', fontWeight: 'bold', minWidth: '100px' }}>
                Group Code:
              </label>
              <Input
                placeholder="e.g., std-checks"
                value={groupCode}
                onChange={this.handleCodeChange}
                status={codeValidationError ? 'error' : ''}
                style={{ flex: 1 }}
                maxLength={10}
              />
            </div>
            {codeValidationError ? (
              <small style={{ color: '#ff4d4f', fontSize: '12px', marginLeft: '108px' }}>
                {codeValidationError}
              </small>
            ) : (
              <small style={{ color: '#999', fontSize: '12px', marginLeft: '108px' }}>
                Lowercase letters, numbers, and hyphens only (URL-safe, max 10 chars)
              </small>
            )}
          </div>

          <Divider style={{ margin: '12px 0' }}>Accreditation Types</Divider>

          <p style={{ marginBottom: '12px', color: '#666', fontSize: '13px' }}>
            When adding credentials to this group, individuals are only required to provide details for one of the accreditations. To add more mandatory accreditations, create another group.
          </p>

          <Form.Item label="Select Accreditation Types">
            <Select
              mode="multiple"
              placeholder="Search and select accreditation types..."
              value={selectedTypes}
              onChange={this.handleTypesChange}
              showSearch
              optionFilterProp="label"
              maxTagCount={0}
              maxTagPlaceholder={selectedTypes.length > 0 ? `${selectedTypes.length} selected` : ''}
              filterOption={(input, option) => {
                return option.props.label.toLowerCase().indexOf(input.toLowerCase()) >= 0;
              }}
              style={{ width: '100%' }}
            >
              {sortedTabs.map(tab => (
                <Select.OptGroup key={tab} label={tab}>
                  {groupedTypes[tab].map(type => (
                    <Select.Option
                      key={type.typeCode}
                      value={type.typeCode}
                      label={`${type.typeName} (${type.typeCode})`}
                    >
                      {type.typeName} ({type.typeCode})
                    </Select.Option>
                  ))}
                </Select.OptGroup>
              ))}
            </Select>
          </Form.Item>

          {selectedTypes.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <div style={{ marginBottom: '8px', color: '#666', fontSize: '13px' }}>
                Selected ({selectedTypes.length}):
              </div>
              <div>
                {selectedTypes.map(typeCode => {
                  const type = accreditationTypes.find(t => t.typeCode === typeCode);
                  return (
                    <Tag
                      key={typeCode}
                      closable
                      onClose={() => {
                        this.setState({
                          selectedTypes: selectedTypes.filter(t => t !== typeCode),
                        });
                      }}
                      style={{ marginBottom: '4px' }}
                    >
                      {type ? type.typeName : typeCode}
                    </Tag>
                  );
                })}
              </div>
            </div>
          )}

          {/* Can be skipped setting - Before creating */}
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
            <Checkbox
              checked={this.state.isOptional}
              onChange={this.handleOptionalChange}
            >
              Can be skipped
            </Checkbox>
            <Tooltip title="When enabled, users can skip this accreditation group during screening without providing any details.">
              <Icon type="info-circle" style={{ marginLeft: '6px', color: '#1890ff', cursor: 'help' }} />
            </Tooltip>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
            <Button onClick={this.handleCancel}>
              Cancel
            </Button>
            <Button type="primary" onClick={this.handleSave} loading={isSaving}>
              Save Group
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

GroupCreationPanel.propTypes = {
  accreditationTypes: PropTypes.array.isRequired,
  existingGroups: PropTypes.array.isRequired,
  selectedGroupCodes: PropTypes.array,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

GroupCreationPanel.defaultProps = {
  selectedGroupCodes: [],
};

export default GroupCreationPanel;
